<template>
  <b-modal
    :visible="visible"
    size="xl"
    centered
    no-close-on-backdrop
    modal-class="wl-register-visit-modal"
    body-class="wl-register-visit-modal-body"
    header-class="wl-register-visit-modal-header"
    footer-class="wl-register-visit-modal-footer"
    dialog-class="wl-register-visit-modal-dialog"
    content-class="wl-register-visit-modal-content"
    @change="val => $emit('update:visible', val)"
    @hidden="onHidden"
    @shown="onShown">
    <template #modal-header="{ close }">
      <div class="wl-register-visit-header w-100 d-flex align-items-center">
        <i class="fas fa-syringe wl-register-visit-header-icon text-muted"></i>
        <div class="flex-grow-1">
          <h5 class="mb-0 font-weight-bolder text-dark">Register GLP-1 Visit</h5>
          <span class="text-muted small">New clinical visit record</span>
        </div>
        <button type="button" class="close" aria-label="Close" @click="close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </template>

    <div class="wl-register-visit-form">
      <section class="wl-register-visit-block">
        <div class="wl-register-visit-block-label">
          <i class="fas fa-user-injured"></i>
          Patient &amp; treatment
        </div>
        <b-row class="wl-register-visit-row">
          <b-col cols="12" lg="5">
            <b-form-group label="Patient" label-class="wl-field-label" class="wl-form-group-compact">
              <treeselect
                v-model="form.weightloss_patient_id"
                :options="patientOptions"
                :async="true"
                :load-options="loadPatients"
                placeholder="Search patient..."
                @input="onPatientChange" />
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6" lg="3">
            <b-form-group label="Treatment" label-class="wl-field-label" class="wl-form-group-compact">
              <treeselect
                v-model="form.weightloss_treatment_id"
                :options="treatmentOptions"
                placeholder="Treatment"
                @input="onTreatmentChange" />
            </b-form-group>
          </b-col>
          <b-col cols="12" sm="6" lg="4">
            <b-form-group label="Dose" label-class="wl-field-label" class="wl-form-group-compact">
              <treeselect
                v-model="form.weightloss_dose_id"
                :options="doseOptions"
                placeholder="Dose"
                @input="scheduleQuote" />
            </b-form-group>
          </b-col>
        </b-row>
      </section>

      <section class="wl-register-visit-block">
        <div class="wl-register-visit-block-label">
          <i class="fas fa-chart-line"></i>
          Measurements &amp; doses
        </div>
        <b-row class="wl-register-visit-row">
          <b-col cols="6" md="3">
            <b-form-group label="Visit date" label-class="wl-field-label" class="wl-form-group-compact">
              <flat-pickr
                v-model="form.visit_date"
                :config="dateConfig"
                class="form-control"
                placeholder="MM-DD-YYYY"
                @on-change="onVisitDateChange" />
              <small class="text-muted">MM-DD-YYYY</small>
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3">
            <b-form-group label="Qty doses (1–4)" label-class="wl-field-label" class="wl-form-group-compact">
              <b-form-select
                v-model="form.qty_doses"
                :options="qtyDoseOptions"
                @change="scheduleQuote" />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3">
            <b-form-group label="Weight (kg)" label-class="wl-field-label" class="wl-form-group-compact">
              <b-form-input v-model="form.weight" type="number" step="0.1" />
            </b-form-group>
          </b-col>
          <b-col cols="6" md="3">
            <b-form-group
              :label="isInsuranceLane ? 'Height (in)' : 'Height (cm)'"
              label-class="wl-field-label"
              class="wl-form-group-compact">
              <b-form-input
                v-model="form.height_value"
                type="number"
                step="0.1"
                placeholder="BMI auto" />
            </b-form-group>
          </b-col>
        </b-row>
      </section>

      <section class="wl-register-visit-block">
        <div class="wl-register-visit-block-label">
          <i class="fas fa-plus-circle"></i>
          Add-ons &amp; notes
        </div>
        <b-row class="wl-register-visit-row">
          <b-col cols="12">
            <div v-if="addonRows.length" class="mb-2">
              <div
                v-for="(row, index) in addonRows"
                :key="row._key"
                class="d-flex flex-wrap align-items-end mb-2">
                <b-form-group
                  label="Add-on"
                  label-class="wl-field-label"
                  class="wl-form-group-compact flex-grow-1 mr-2 mb-2">
                  <treeselect
                    v-model="row.weightloss_addon_id"
                    :options="addonOptions"
                    placeholder="Select add-on"
                    @input="scheduleQuote" />
                </b-form-group>
                <b-form-group
                  label="Qty"
                  label-class="wl-field-label"
                  class="wl-form-group-compact mr-2 mb-2"
                  style="min-width: 5rem">
                  <b-form-input
                    v-model="row.qty"
                    type="number"
                    min="1"
                    step="1"
                    @input="scheduleQuote" />
                </b-form-group>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="mb-2"
                  @click="removeAddonRow(index)">
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </div>
            <b-button variant="outline-primary" size="sm" class="mb-2" @click="addAddonRow">
              <i class="fas fa-plus mr-1"></i> Add add-on
            </b-button>
            <b-form-group label="Notes" label-class="wl-field-label" class="wl-form-group-compact mb-0">
              <b-form-textarea v-model="form.notes" rows="2" placeholder="Optional" />
            </b-form-group>
          </b-col>
        </b-row>
      </section>

      <section class="wl-register-visit-block">
        <div class="wl-register-visit-block-label">
          <i class="fas fa-receipt"></i>
          Pricing preview
        </div>
        <div v-if="quoteLoading" class="text-muted small py-2">Calculating…</div>
        <b-alert v-else-if="quoteError" show variant="warning" class="mb-0 py-2">
          {{ quoteError }}
        </b-alert>
        <div v-else-if="pricing" class="wl-pricing-preview">
          <div v-if="pricing.main_line" class="d-flex justify-content-between small mb-1">
            <span>
              {{ treatmentLabel }}
              <span v-if="pricing.main_line.dose_label"> — {{ pricing.main_line.dose_label }}</span>
              <span class="text-muted">
                · {{ pricing.main_line.qty_doses }} dosis
                <template v-if="pricing.main_line.pricing_source === 'unit' && pricing.main_line.unit_price">
                  · ${{ pricing.main_line.unit_price }}
                </template>
                <template v-else-if="pricing.main_line.pricing_source">
                  · {{ pricing.main_line.pricing_source }}
                </template>
              </span>
            </span>
            <strong>${{ pricing.main_line.line_total }}</strong>
          </div>
          <template v-if="pricing.addons && pricing.addons.length">
            <div class="font-weight-bold small mt-2 mb-1">
              {{ pricing.addons_group_label || 'Add-ons' }}
            </div>
            <div
              v-for="(a, i) in pricing.addons"
              :key="'addon-' + i"
              class="d-flex justify-content-between small mb-1 pl-2">
              <span>
                {{ a.name }}
                <span class="text-muted">
                  · {{ a.qty }} aplic.
                  <template v-if="a.unit_price"> · ${{ a.unit_price }}</template>
                </span>
              </span>
              <strong>${{ a.line_total }}</strong>
            </div>
          </template>
          <hr class="my-2" />
          <div class="d-flex justify-content-between">
            <span class="font-weight-bold">Total a cobrar</span>
            <span class="font-weight-bolder text-primary">${{ pricing.total }}</span>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">
          Select patient, treatment, dose and qty to preview pricing.
        </p>
      </section>

      <section class="wl-register-visit-block wl-register-visit-block-last">
        <div class="wl-register-visit-block-label">
          <i class="fas fa-hospital"></i>
          Location &amp; provider
        </div>
        <b-row class="wl-register-visit-row mb-0">
          <b-col cols="12" md="6">
            <b-form-group label="Facility" label-class="wl-field-label" class="wl-form-group-compact mb-md-0">
              <treeselect
                v-model="form.facility_id"
                :options="facilityOptions"
                :normalizer="normalizer"
                :clearable="false"
                placeholder="Select facility"
                :disabled="facilityOptionsLoading"
                @input="onFacilityChange" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Provider ID" label-class="wl-field-label" class="wl-form-group-compact mb-0">
              <b-form-input v-model="form.provider_id" type="number" placeholder="Doctor user ID" />
            </b-form-group>
          </b-col>
        </b-row>
      </section>
    </div>

    <template #modal-footer>
      <div class="wl-register-visit-footer-inner w-100">
        <div class="wl-register-visit-injection">
          <b-form-checkbox v-model="form.injection_given" switch class="mb-0">
            <span class="font-weight-bold">Injection given</span>
          </b-form-checkbox>
          <span class="text-muted small d-none d-md-inline ml-2">
            — auto-deducts inventory
          </span>
          <div
            v-if="form.injection_given && !selectedDoseHasItem"
            class="wl-register-visit-warning small text-muted mt-1">
            <i class="fas fa-exclamation-circle mr-1"></i>
            Dose has no linked inventory item
          </div>
        </div>
        <div class="wl-register-visit-actions">
          <b-button variant="outline-secondary" :disabled="saving" @click="closeModal">
            Cancel
          </b-button>
          <b-button variant="primary" :disabled="saving || !!quoteError" @click="submit">
            <SpinnerLoading v-if="saving" />
            <span v-else><i class="fas fa-check mr-1"></i> Register visit</span>
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormSelect,
  BButton,
  BFormTextarea,
  BAlert,
} from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import { ASYNC_SEARCH } from '@riophae/vue-treeselect'
import flatPickr from 'vue-flatpickr-component'
import SpinnerLoading from '@/components/SpinnerLoading'
import wlTreatmentsApi from '@/core/services/api/weightloss-inventory/treatments'
import wlDosesApi from '@/core/services/api/weightloss-inventory/doses'
import wlAddonsApi from '@/core/services/api/weightloss-inventory/addons'
import wlPricingApi from '@/core/services/api/weightloss-inventory/pricing'
import { resolveNumericFacilityId } from '@/core/services/api/weightloss-inventory/facility'
import { todayProjectDate, toProjectDate } from '@/core/services/api/weightloss-inventory/dateFormat'
import axiosF from '@/core/services/api/admin/facilities'
import { getWlPatientsApi, getWlVisitsApi } from '@/core/services/api/weightloss-inventory/laneApi'

function resolveProviderId() {
  try {
    const u = JSON.parse(localStorage.getItem('userData'))
    return (u && u.id) || ''
  } catch {
    return ''
  }
}

function defaultForm() {
  const defaultFacility = resolveNumericFacilityId()
  return {
    weightloss_patient_id: null,
    weightloss_treatment_id: null,
    weightloss_dose_id: null,
    qty_doses: 1,
    facility_id: defaultFacility ? Number(defaultFacility) : null,
    provider_id: resolveProviderId(),
    visit_date: todayProjectDate(),
    weight: '',
    height_value: '',
    injection_given: false,
    notes: '',
  }
}

let addonRowKey = 0
function newAddonRow() {
  addonRowKey += 1
  return {
    _key: addonRowKey,
    weightloss_addon_id: null,
    qty: 1,
  }
}

export default {
  name: 'RegisterWlVisitModal',
  components: {
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormSelect,
    BButton,
    BFormTextarea,
    BAlert,
    Treeselect,
    flatPickr,
    SpinnerLoading,
  },
  props: {
    visible: { type: Boolean, default: false },
  },
  computed: {
    programContext() {
      const path = (this.$route && this.$route.path) || ''
      if (path.startsWith('/insurance-weightloss')) return 'insurance'
      return (this.$route && this.$route.meta && this.$route.meta.programContext) || 'normal'
    },
    isInsuranceLane() {
      return this.programContext === 'insurance'
    },
    patientsApi() {
      return getWlPatientsApi(this.programContext)
    },
    visitsApi() {
      return getWlVisitsApi(this.programContext)
    },
  },
  data() {
    return {
      saving: false,
      patientOptions: [],
      treatmentOptions: [],
      doseOptions: [],
      dosesRaw: [],
      addonOptions: [],
      addonsRaw: [],
      addonRows: [],
      facilityOptions: [],
      facilityOptionsLoading: false,
      form: defaultForm(),
      pricing: null,
      quoteLoading: false,
      quoteError: '',
      quoteTimer: null,
      qtyDoseOptions: [1, 2, 3, 4].map(n => ({ value: n, text: String(n) })),
      dateConfig: {
        dateFormat: 'm-d-Y',
        allowInput: true,
        altInput: false,
      },
      normalizer(node) {
        return { id: node.id, label: node.name }
      },
    }
  },
  computed: {
    selectedDoseHasItem() {
      const d = this.dosesRaw.find(x => x.id === this.form.weightloss_dose_id)
      return d && d.inv_item_id
    },
    treatmentLabel() {
      const t = this.treatmentOptions.find(x => x.id === this.form.weightloss_treatment_id)
      return (t && t.label) || 'Treatment'
    },
  },
  methods: {
    onVisitDateChange(selectedDates, dateStr) {
      const raw = dateStr || (selectedDates && selectedDates[0]) || this.form.visit_date
      this.form.visit_date = toProjectDate(raw) || ''
      this.scheduleQuote()
    },
    onShown() {
      if (!this.treatmentOptions.length) {
        wlTreatmentsApi.list().then(t => {
          this.treatmentOptions = t.map(x => ({ id: x.id, label: x.name }))
        })
      }
      if (!this.facilityOptions.length) {
        this.loadFacilities()
      }
      this.loadAddons()
    },
    onHidden() {
      this.resetForm()
    },
    resetForm() {
      if (this.quoteTimer) clearTimeout(this.quoteTimer)
      this.form = defaultForm()
      this.doseOptions = []
      this.dosesRaw = []
      this.patientOptions = []
      this.addonRows = []
      this.addonOptions = []
      this.addonsRaw = []
      this.pricing = null
      this.quoteError = ''
      this.quoteLoading = false
    },
    closeModal() {
      this.$emit('update:visible', false)
    },
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(item => ({
          id: item.id,
          name: item.name,
        }))
      } catch {
        this.facilityOptions = []
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    loadPatients({ action, searchQuery, callback }) {
      if (action !== ASYNC_SEARCH) return callback(null, [])
      const params = this.facilityParams()
      if (searchQuery) params.search = searchQuery
      this.patientsApi
        .listPaginated(20, params, 1)
        .then(res => {
          const options = res.items.map(p => ({
            id: p.id,
            label: `${p.first_name} ${p.last_name} (#${p.id})`,
            treatment_id: p.weightloss_treatment_id,
          }))
          callback(null, options)
        })
        .catch(() => callback(null, []))
    },
    facilityParams() {
      const id = this.form.facility_id
      return id ? { facility_id: id } : {}
    },
    onPatientChange(patientId) {
      const p = this.patientOptions.find(o => o.id === patientId)
      if (p && p.treatment_id) {
        this.form.weightloss_treatment_id = p.treatment_id
        this.onTreatmentChange()
      } else {
        this.scheduleQuote()
      }
    },
    onTreatmentChange() {
      this.form.weightloss_dose_id = null
      this.loadDoses().then(() => this.scheduleQuote())
    },
    onFacilityChange() {
      this.loadAddons()
      this.scheduleQuote()
    },
    async loadDoses() {
      if (!this.form.weightloss_treatment_id) {
        this.doseOptions = []
        this.dosesRaw = []
        this.form.weightloss_dose_id = null
        return
      }
      this.dosesRaw = await wlDosesApi.list({
        weightloss_treatment_id: this.form.weightloss_treatment_id,
        active: true,
      })
      this.doseOptions = this.dosesRaw.map(d => ({
        id: d.id,
        label: d.dose_label,
      }))
    },
    async loadAddons() {
      const params = { active: true }
      if (this.form.facility_id) params.facility_id = Number(this.form.facility_id)
      try {
        this.addonsRaw = await wlAddonsApi.list(params)
        this.addonOptions = this.addonsRaw.map(a => {
          const hasPrice = a.price != null && a.price !== ''
          return {
            id: a.id,
            label: hasPrice
              ? `${a.name} ($${Number(a.price).toFixed(2)})`
              : `${a.name} (no price — not chargeable)`,
            isDisabled: !hasPrice,
          }
        })
      } catch {
        this.addonsRaw = []
        this.addonOptions = []
      }
    },
    addAddonRow() {
      this.addonRows.push(newAddonRow())
    },
    removeAddonRow(index) {
      this.addonRows.splice(index, 1)
      this.scheduleQuote()
    },
    buildAddonsPayload() {
      return this.addonRows
        .filter(row => row.weightloss_addon_id)
        .map(row => ({
          weightloss_addon_id: Number(row.weightloss_addon_id),
          qty: row.qty != null && row.qty !== '' ? Number(row.qty) : 1,
        }))
        .filter(row => Number.isFinite(row.qty) && row.qty > 0)
    },
    scheduleQuote() {
      if (this.quoteTimer) clearTimeout(this.quoteTimer)
      this.quoteTimer = setTimeout(() => this.fetchQuote(), 350)
    },
    canQuote() {
      return (
        this.form.weightloss_treatment_id &&
        this.form.weightloss_dose_id &&
        this.form.qty_doses &&
        this.form.visit_date
      )
    },
    buildQuotePayload() {
      const visitDate = toProjectDate(this.form.visit_date)
      const payload = {
        weightloss_treatment_id: Number(this.form.weightloss_treatment_id),
        weightloss_dose_id: Number(this.form.weightloss_dose_id),
        qty_doses: Number(this.form.qty_doses),
        visit_date: visitDate,
      }
      if (this.form.facility_id != null && this.form.facility_id !== '') {
        payload.facility_id = Number(this.form.facility_id)
      } else {
        payload.facility_id = null
      }
      if (this.form.weightloss_patient_id) {
        payload.weightloss_patient_id = Number(this.form.weightloss_patient_id)
      }
      const addons = this.buildAddonsPayload()
      if (addons.length) payload.addons = addons
      return payload
    },
    async fetchQuote() {
      if (!this.canQuote()) {
        this.pricing = null
        this.quoteError = ''
        return
      }
      const visitDate = toProjectDate(this.form.visit_date)
      if (!visitDate || !/^\d{2}-\d{2}-\d{4}$/.test(visitDate)) {
        this.pricing = null
        this.quoteError = 'Visit date must be MM-DD-YYYY (e.g. 07-15-2026)'
        return
      }
      this.quoteLoading = true
      this.quoteError = ''
      try {
        this.pricing = await wlPricingApi.quote(this.buildQuotePayload())
      } catch (e) {
        this.pricing = null
        this.quoteError = e.message || 'Could not calculate pricing'
      } finally {
        this.quoteLoading = false
      }
    },
    async submit() {
      if (!this.form.weightloss_patient_id) {
        this.$emit('error', 'Patient is required')
        return
      }
      if (!this.canQuote()) {
        this.$emit('error', 'Treatment, dose, qty doses and visit date are required')
        return
      }
      const visitDate = toProjectDate(this.form.visit_date)
      if (!visitDate || !/^\d{2}-\d{2}-\d{4}$/.test(visitDate)) {
        this.$emit('error', 'Visit date must be MM-DD-YYYY (e.g. 07-15-2026)')
        return
      }
      this.form.visit_date = visitDate
      if (this.quoteError) {
        this.$emit('error', this.quoteError)
        return
      }

      this.saving = true
      try {
        const payload = {
          weightloss_patient_id: Number(this.form.weightloss_patient_id),
          weightloss_treatment_id: Number(this.form.weightloss_treatment_id),
          weightloss_dose_id: Number(this.form.weightloss_dose_id),
          qty_doses: Number(this.form.qty_doses),
          facility_id: Number(this.form.facility_id),
          provider_id: Number(this.form.provider_id),
          visit_date: visitDate,
          weight: Number(this.form.weight),
          injection_given: !!this.form.injection_given,
        }
        if (this.form.height_value !== '' && this.form.height_value != null) {
          // Contrato insurance: height_in. Regular: height_cm.
          if (this.isInsuranceLane) {
            payload.height_in = Number(this.form.height_value)
          } else {
            payload.height_cm = Number(this.form.height_value)
          }
        }
        if (this.form.notes?.trim()) payload.notes = this.form.notes.trim()
        const addons = this.buildAddonsPayload()
        if (addons.length) payload.addons = addons

        await this.visitsApi.create(payload)
        this.$emit('registered')
        this.closeModal()
      } catch (e) {
        this.$emit('error', e.message || 'Could not register visit')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
::v-deep .wl-register-visit-modal .modal-dialog {
  max-width: 860px;
  margin: 1.75rem auto;
}

::v-deep .wl-register-visit-modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(82, 63, 105, 0.18);
  overflow: visible;
}

::v-deep .wl-register-visit-modal-header {
  border-bottom: 1px solid #eff2f5;
  padding: 1rem 1.5rem;
  background: #fff;
  border-radius: 12px 12px 0 0;
}

::v-deep .wl-register-visit-modal-body {
  padding: 0;
  overflow: visible !important;
  max-height: none !important;
}

::v-deep .wl-register-visit-modal-footer {
  border-top: 1px solid #eff2f5;
  padding: 0.85rem 1.5rem;
  background: #fff;
  border-radius: 0 0 12px 12px;
}

.wl-register-visit-header-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-right: 0.75rem;
}

.wl-register-visit-form {
  padding: 0.25rem 0;
}

.wl-register-visit-block {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eff2f5;
}

.wl-register-visit-block-last {
  border-bottom: none;
}

.wl-register-visit-block-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #3f4254;
  margin-bottom: 0.75rem;
}

.wl-register-visit-block-label i {
  margin-right: 0.45rem;
  color: #7e8299;
}

.wl-register-visit-row {
  margin-left: -0.5rem;
  margin-right: -0.5rem;
}

.wl-register-visit-row > [class*='col-'] {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

::v-deep .wl-form-group-compact {
  margin-bottom: 0;
}

::v-deep .wl-field-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #5e6278;
  margin-bottom: 0.25rem;
}

::v-deep .wl-register-visit-modal-body .vue-treeselect__control {
  min-height: 36px;
  border-radius: 6px;
  border-color: #e4e6ef;
  font-size: 0.9rem;
}

::v-deep .wl-register-visit-modal-body .form-control {
  height: 36px;
  border-radius: 6px;
  border-color: #e4e6ef;
  font-size: 0.9rem;
}

.wl-pricing-preview {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.wl-register-visit-footer-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.wl-register-visit-injection {
  flex: 1 1 auto;
  min-width: 0;
}

.wl-register-visit-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.wl-register-visit-warning {
  line-height: 1.3;
}

@media (max-width: 767.98px) {
  ::v-deep .wl-register-visit-modal .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }

  .wl-register-visit-block {
    padding: 0.85rem 1rem;
  }

  .wl-register-visit-footer-inner {
    flex-direction: column;
    align-items: stretch;
  }

  .wl-register-visit-actions {
    justify-content: flex-end;
  }
}
</style>
